import React from 'react'
import { Box, Tooltip, useTheme } from '@mui/material'

import standardIndicators from '../../condition/indicators/standard'
import gtfIndicators from '../../condition/indicators/thirdparty/gtf'
import { SolvedChart, SolvedSeries } from '../solve/assignValues'
import { BadgeSpec, SeriesId } from '../types'
import { GtfColor, getRenderMeta } from './renderMeta'
import { visibleBars } from './visibleBars'

const PRICE_PANE_HEIGHT = 140
const MINI_PANE_HEIGHT = 50
const PANE_GAP = 8
const LABEL_WIDTH = 88
const MAX_EDGE_LABELS = 5

interface PreviewChartProps {
  chart: SolvedChart
  conditionLabels: Record<string, string>
  width?: number
}

interface Pane {
  key: string
  height: number
  series: SolvedSeries[]
  withCandles: boolean
  fixedRange?: [number, number]
}

const seriesHash = (seriesId: SeriesId): string => seriesId.slice(0, seriesId.indexOf('.'))

const REGISTRY_BY_VALUE = new Map(
  [...standardIndicators, ...gtfIndicators].map((indicator) => [indicator.value, indicator])
)

const reverseCamelCase = (value: string): string =>
  value
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (first) => first.toUpperCase())
    .trim()

const seriesLabel = (
  seriesId: SeriesId,
  info: SolvedChart['seriesInfo'][string] | undefined
): string => {
  if (!info) return seriesId
  if (seriesId.startsWith('candle.')) return reverseCamelCase(info.field)
  const entry = REGISTRY_BY_VALUE.get(info.indicatorName)
  const name = entry?.nickname ?? entry?.label ?? reverseCamelCase(info.indicatorName)
  const period = info.params?.period
  const withPeriod = period !== undefined ? `${name}(${period})` : name
  if (info.field.toLowerCase() === info.indicatorName.toLowerCase()) return withPeriod
  const humanField = reverseCamelCase(info.field)
  // Fields like 'greenPressure' already name the indicator — they stand alone
  if (info.field.toLowerCase().includes(info.indicatorName.toLowerCase())) {
    return period !== undefined ? `${humanField} (${period})` : humanField
  }
  return `${withPeriod} ${humanField}`
}

/**
 * Static SVG snapshot of one chart key: gray/entailed candles on the price
 * pane, one polyline per deduped indicator line, oscillator mini-panes, and
 * badges where each condition bites. Pure presentation over SolvedChart.
 */
const PreviewChart = ({ chart, conditionLabels, width = 560 }: PreviewChartProps) => {
  const theme = useTheme()

  const gtfColorFor = (color: GtfColor | undefined): string | undefined => {
    switch (color) {
      case 'blue':
        return theme.palette.info.main
      case 'orange':
        return theme.palette.warning.main
      case 'red':
        return theme.palette.error.main
      case 'green':
        return theme.palette.success.main
      case 'gray':
        return theme.palette.text.secondary
      case 'black':
        return theme.palette.text.primary
      default:
        return undefined
    }
  }
  const linePalette = [
    theme.palette.info.main,
    theme.palette.primary.main,
    theme.palette.warning.main,
    theme.palette.secondary?.main ?? theme.palette.info.main,
  ]

  const bars = visibleBars(chart)
  const plotWidth = width - LABEL_WIDTH
  const step = plotWidth / Math.max(bars.length, 1)
  const barX = new Map<number, number>()
  bars.forEach((bar, i) => barX.set(bar, i * step + step / 2))

  // --- pane assembly: price pane + one mini-pane per own-pane indicator hash ---
  const pricePaneSeries: SolvedSeries[] = []
  const ownPanes = new Map<string, SolvedSeries[]>()
  chart.series.forEach((series) => {
    const info = chart.seriesInfo[series.seriesId]
    const meta = getRenderMeta(info?.indicatorName ?? '', info?.field ?? '')
    if (meta.pane === 'price') {
      pricePaneSeries.push(series)
    } else {
      const hash = seriesHash(series.seriesId)
      ownPanes.set(hash, [...(ownPanes.get(hash) ?? []), series])
    }
  })

  const panes: Pane[] = [
    {
      key: 'price',
      height: PRICE_PANE_HEIGHT,
      series: pricePaneSeries,
      withCandles: true,
    },
    ...Array.from(ownPanes.entries()).map(([hash, series]) => {
      const info = chart.seriesInfo[series[0].seriesId]
      const meta = getRenderMeta(info?.indicatorName ?? '', info?.field ?? '')
      return {
        key: hash,
        height: MINI_PANE_HEIGHT,
        series,
        withCandles: false,
        fixedRange: meta.fixedRange,
      }
    }),
  ]
  const totalHeight = panes.reduce((sum, pane) => sum + pane.height + PANE_GAP, 0)

  const candleColorFor = (color: 'green' | 'red' | 'gray' | 'white'): string => {
    if (color === 'green') return theme.palette.success.main
    if (color === 'red') return theme.palette.error.main
    if (color === 'white') return theme.palette.common.white
    return theme.palette.text.secondary
  }
  /** wick and body share the exact same color AND opacity per candle state */
  const candleOpacityFor = (color: 'green' | 'red' | 'gray' | 'white'): number =>
    color === 'gray' ? 0.45 : 0.9

  const badgeGlyph = (badge: BadgeSpec): string => {
    switch (badge.kind) {
      case 'cross':
        return '✕'
      case 'up':
        return '↑'
      case 'down':
        return '↓'
      case 'gapBracket':
        return '⟷'
      case 'window':
        return '🕐'
      case 'prereq':
        return '🔗'
      default:
        return ''
    }
  }

  const isSquaresPane = (pane: Pane): boolean => {
    const info = chart.seriesInfo[pane.series[0]?.seriesId]
    return getRenderMeta(info?.indicatorName ?? '', info?.field ?? '').style === 'squares'
  }

  /**
   * Echo-style run-length row: the solved value at a pinned bar IS the count
   * of consecutive same-color bars ending there (read right-to-left), so a
   * pinned greenEcho of 3 paints that bar and the 2 before it green. Bars no
   * condition touches stay gray, matching the candle rule.
   */
  const renderSquares = (pane: Pane, top: number): React.ReactNode => {
    const runColors: Array<'green' | 'red' | null> = new Array(chart.axisLength).fill(null)
    pane.series.forEach((series) => {
      const field = chart.seriesInfo[series.seriesId]?.field ?? ''
      const isGreenField = field.toLowerCase().startsWith('green')
      for (let bar = 0; bar < chart.axisLength; bar += 1) {
        if (!series.pinned[bar]) continue
        const value = series.values[bar]
        const color: 'green' | 'red' =
          value > 0 ? (isGreenField ? 'green' : 'red') : isGreenField ? 'red' : 'green'
        const run = Math.max(1, Math.floor(Math.abs(value)))
        for (let back = 0; back < run && bar - back >= 0; back += 1) {
          runColors[bar - back] = color
        }
      }
    })
    const squareSize = Math.min(MINI_PANE_HEIGHT * 0.45, Math.max(4, step * 0.55))
    const squareY = top + (MINI_PANE_HEIGHT - squareSize) / 2
    return bars.map((bar) => {
      const color = runColors[bar]
      return (
        <rect
          key={`square-${bar}`}
          data-testid="echo-square"
          data-color={color ?? 'gray'}
          x={(barX.get(bar) as number) - squareSize / 2}
          y={squareY}
          width={squareSize}
          height={squareSize}
          rx={1}
          fill={color ? candleColorFor(color) : theme.palette.text.secondary}
          fillOpacity={color ? 0.85 : 0.35}
        />
      )
    })
  }

  let paneTop = 0
  const renderedPanes = panes.map((pane, paneIndex) => {
    const top = paneTop
    paneTop += pane.height + PANE_GAP

    // y-domain: fixed oscillator range, else min/max over drawn values.
    let lo = pane.fixedRange?.[0] ?? Infinity
    let hi = pane.fixedRange?.[1] ?? -Infinity
    if (!pane.fixedRange) {
      pane.series.forEach((series) =>
        bars.forEach((bar) => {
          const value = series.values[bar]
          if (Number.isNaN(value)) return
          lo = Math.min(lo, value)
          hi = Math.max(hi, value)
        })
      )
      if (pane.withCandles) {
        bars.forEach((bar) => {
          const candle = chart.candles[bar]
          if (!candle) return
          lo = Math.min(lo, candle.low)
          hi = Math.max(hi, candle.high)
        })
      }
      if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
        lo = 0
        hi = 100
      }
      const pad = (hi - lo || 10) * 0.08
      lo -= pad
      hi += pad
    }
    const y = (value: number): number =>
      top + pane.height - ((value - lo) / (hi - lo || 1)) * pane.height

    const candleWidth = Math.max(2, step * 0.55)

    const paneBadges = chart.badges.filter((badge) => {
      if (paneIndex !== 0) {
        return (
          badge.seriesId !== undefined && pane.series.some((s) => s.seriesId === badge.seriesId)
        )
      }
      return (
        badge.seriesId === undefined ||
        badge.seriesId.startsWith('candle.') ||
        pane.series.some((s) => s.seriesId === badge.seriesId)
      )
    })

    // Right-edge label chips with simple collision nudging.
    const edgeLabels = pane.series
      .map((series) => {
        const lastBar = bars[bars.length - 1]
        const value = series.values[lastBar]
        return { seriesId: series.seriesId, y: Number.isNaN(value) ? top : y(value) }
      })
      .sort((a, b) => a.y - b.y)
      .slice(0, MAX_EDGE_LABELS)
    for (let i = 1; i < edgeLabels.length; i += 1) {
      if (edgeLabels[i].y - edgeLabels[i - 1].y < 12) {
        edgeLabels[i].y = edgeLabels[i - 1].y + 12
      }
    }

    return (
      <g key={pane.key} data-testid={`preview-pane-${pane.key}`}>
        {pane.withCandles &&
          bars.map((bar) => {
            const candle = chart.candles[bar]
            const x = barX.get(bar) as number
            if (!candle) return null
            const bodyTop = y(Math.max(candle.open, candle.close))
            const bodyBottom = y(Math.min(candle.open, candle.close))
            const fill = candleColorFor(candle.color)
            return (
              <g key={`candle-${bar}`}>
                <line
                  x1={x}
                  x2={x}
                  y1={y(candle.high)}
                  y2={y(candle.low)}
                  stroke={fill}
                  strokeOpacity={candleOpacityFor(candle.color)}
                  strokeWidth={1}
                />
                <rect
                  data-testid="candle-body"
                  data-color={candle.color}
                  x={x - candleWidth / 2}
                  y={bodyTop}
                  width={candleWidth}
                  height={Math.max(1.5, bodyBottom - bodyTop)}
                  fill={fill}
                  fillOpacity={candleOpacityFor(candle.color)}
                  rx={1}
                />
              </g>
            )
          })}
        {isSquaresPane(pane)
          ? renderSquares(pane, top)
          : pane.series.map((series, i) => {
              const info = chart.seriesInfo[series.seriesId]
              const meta = getRenderMeta(info?.indicatorName ?? '', info?.field ?? '')
              const stroke = gtfColorFor(meta.gtfColor) ?? linePalette[i % linePalette.length]
              // Gray-until-it-matters, same rule as candles: segments on bars a
              // condition actually pins draw in the series color; the rest gray.
              const segments: Array<{ pinnedSegment: boolean; points: string[] }> = []
              let current: { pinnedSegment: boolean; points: string[] } | null = null
              bars
                .filter((bar) => !Number.isNaN(series.values[bar]))
                .forEach((bar) => {
                  const pinnedBar = series.pinned[bar]
                  const point = `${barX.get(bar)},${y(series.values[bar]).toFixed(2)}`
                  if (!current || current.pinnedSegment !== pinnedBar) {
                    const boundary = current?.points[current.points.length - 1]
                    if (current) segments.push(current)
                    current = {
                      pinnedSegment: pinnedBar,
                      points: boundary ? [boundary, point] : [point],
                    }
                  } else {
                    current.points.push(point)
                  }
                })
              if (current) segments.push(current)
              return segments.map((segment, segmentIndex) => (
                <polyline
                  key={`${series.seriesId}-${segmentIndex}`}
                  data-testid="indicator-line"
                  data-pinned={segment.pinnedSegment}
                  points={segment.points.join(' ')}
                  fill="none"
                  stroke={segment.pinnedSegment ? stroke : theme.palette.text.secondary}
                  strokeOpacity={segment.pinnedSegment ? 1 : 0.45}
                  strokeWidth={meta.thick ? 2.5 : 1.5}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              ))
            })}
        {/* gap glyphs where the broken axis skips bars */}
        {bars.map((bar, i) => {
          if (i === 0 || bars[i - 1] === bar - 1) return null
          const x = ((barX.get(bar) as number) + (barX.get(bars[i - 1]) as number)) / 2
          return (
            <text
              key={`gap-${bar}`}
              x={x}
              y={top + pane.height / 2}
              fill={theme.palette.text.secondary}
              fontSize={10}
              textAnchor="middle"
            >
              ⋯
            </text>
          )
        })}
        {paneBadges.map((badge, i) => {
          const label = conditionLabels[badge.conditionId] ?? ''
          if (badge.kind === 'guide' && badge.bar !== undefined && barX.has(badge.bar)) {
            return (
              <line
                key={`badge-${i}`}
                x1={barX.get(badge.bar)}
                x2={barX.get(badge.bar)}
                y1={top}
                y2={top + pane.height}
                stroke={theme.palette.text.secondary}
                strokeDasharray="3 3"
                strokeOpacity={0.5}
              />
            )
          }
          if ((badge.kind === 'span' || badge.kind === 'window') && badge.window) {
            const fromX = barX.get(Math.max(0, badge.window.from)) ?? step / 2
            const toX = barX.get(badge.window.to) ?? plotWidth - step / 2
            return (
              <Tooltip key={`badge-${i}`} title={label}>
                <line
                  x1={fromX}
                  x2={toX}
                  y1={top + 4}
                  y2={top + 4}
                  stroke={theme.palette.info.main}
                  strokeWidth={3}
                  strokeLinecap="round"
                  strokeOpacity={0.6}
                />
              </Tooltip>
            )
          }
          if (badge.bar === undefined || !barX.has(badge.bar)) return null
          const series = pane.series.find((s) => s.seriesId === badge.seriesId)
          const value = series?.values[badge.bar]
          const badgeY = value !== undefined && !Number.isNaN(value) ? y(value) - 8 : top + 12
          return (
            <Tooltip key={`badge-${i}`} title={label}>
              <text
                data-testid={`badge-${badge.kind}`}
                x={barX.get(badge.bar)}
                y={badgeY}
                fill={theme.palette.text.primary}
                fontSize={11}
                textAnchor="middle"
              >
                {badgeGlyph(badge)}
              </text>
            </Tooltip>
          )
        })}
        {(isSquaresPane(pane)
          ? [{ seriesId: pane.series[0]?.seriesId ?? '', y: top + pane.height / 2 }]
          : edgeLabels
        ).map((edgeLabel) => (
          <text
            key={`label-${edgeLabel.seriesId}`}
            x={plotWidth + 6}
            y={edgeLabel.y + 3}
            fill={theme.palette.text.secondary}
            fontSize={10}
          >
            {seriesLabel(edgeLabel.seriesId, chart.seriesInfo[edgeLabel.seriesId])}
          </text>
        ))}
      </g>
    )
  })

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <svg
        data-testid="preview-chart"
        width={width}
        height={totalHeight}
        viewBox={`0 0 ${width} ${totalHeight}`}
        style={{ display: 'block', maxWidth: '100%' }}
      >
        {renderedPanes}
      </svg>
    </Box>
  )
}

// The panel re-renders on every keystroke (context subscription); chart and
// labels are reference-stable between debounced solves, so memo skips the
// full pane/segment/badge SVG assembly on keystrokes that change nothing.
export default React.memo(PreviewChart)
